import { useState, useEffect, useCallback } from 'react';
import {
  BrainCircuit, Loader2, RefreshCw, AlertTriangle,
  TrendingUp, TrendingDown, Minus, ShieldAlert,
  CheckCircle2, HelpCircle, ChevronRight, Users,
  Lightbulb, Target, Copy, Check,
} from 'lucide-react';
import { generatePrepBrief, getPrepBrief, MavisApiError } from '../../lib/mavisApi';
import { captureEdgeFunctionError } from '../../lib/sentry';
import type {
  MavisOpportunityPrepBrief,
  DealHealth,
  PrepBriefQualificationGap,
  PrepBriefStakeholderRisk,
  PrepBriefNextAction,
  MavisStakeholder,
} from '../../types/mavis';
import MavisApplySuggestionsPanel from './MavisApplySuggestionsPanel';

// Report a failed MAVIS call and turn it into the message shown to the user.
function toUserError(err: unknown): unknown {
  if (!(err instanceof MavisApiError)) return err;
  captureEdgeFunctionError('mavis-opportunity-prep', err.status, JSON.stringify(err.body));
  return new Error(typeof err.body?.error === 'string' && err.body.error ? err.body.error : `Server error (${err.status})`);
}

// ── Deal health chip ───────────────────────────────────────────────────────────

const DEAL_HEALTH_CONFIG: Record<DealHealth, { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  strong:   { label: 'Strong',   cls: 'bg-forest-100 text-forest-700 border-forest-200', icon: TrendingUp },
  moderate: { label: 'Moderate', cls: 'bg-amber-100 text-amber-700 border-amber-200',   icon: Minus },
  weak:     { label: 'Weak',     cls: 'bg-orange-100 text-orange-700 border-orange-200', icon: TrendingDown },
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700 border-red-200',         icon: AlertTriangle },
};

function DealHealthChip({ health }: { health: DealHealth }) {
  const cfg = DEAL_HEALTH_CONFIG[health] ?? DEAL_HEALTH_CONFIG.moderate;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${cfg.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
}

// ── Severity chip ──────────────────────────────────────────────────────────────

const SEVERITY_CLS: Record<string, string> = {
  high:   'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-app-canvas text-app-muted border-app-line',
};

function SeverityChip({ severity }: { severity: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${SEVERITY_CLS[severity] ?? SEVERITY_CLS.low}`}>
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
}

// ── Confidence meter ───────────────────────────────────────────────────────────

function ConfidenceMeter({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value));
  const color = pct >= 70 ? 'bg-forest-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-app-canvas rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-app-muted w-9 text-right">{pct}%</span>
    </div>
  );
}

// ── Section card wrapper ───────────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, children, action }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
        <Icon className="w-4 h-4 text-app-muted" />
        <h3 className="text-sm font-semibold text-app-ink flex-1">{title}</h3>
        {action}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

// ── Copy button ────────────────────────────────────────────────────────────────

function CopyButton({ onCopy, copied }: { onCopy: () => void; copied: boolean }) {
  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-1 text-[11px] font-medium text-app-muted hover:text-forest-600 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <>
          <Check className="w-3 h-3 text-forest-500" />
          <span className="text-forest-600">Copied</span>
        </>
      ) : (
        <>
          <Copy className="w-3 h-3" />
          Copy
        </>
      )}
    </button>
  );
}

// ── Brief display ──────────────────────────────────────────────────────────────

function PrepBriefDisplay({
  brief,
  generatedAt,
}: {
  brief: MavisOpportunityPrepBrief;
  generatedAt: string;
}) {
  const [copiedQuestions, setCopiedQuestions] = useState(false);
  const [copiedActions,   setCopiedActions]   = useState(false);

  const gaps      = Array.isArray(brief.qualification_gaps)       ? brief.qualification_gaps       : [];
  const risks     = Array.isArray(brief.stakeholder_risks)        ? brief.stakeholder_risks        : [];
  const questions = Array.isArray(brief.recommended_questions)    ? brief.recommended_questions    : [];
  const actions   = Array.isArray(brief.recommended_next_actions) ? brief.recommended_next_actions : [];

  const copyQuestions = async () => {
    const text = questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedQuestions(true);
      setTimeout(() => setCopiedQuestions(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const copyActions = async () => {
    const text = actions
      .map((a, i) => `${i + 1}. ${a.action}\n   ${a.rationale}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedActions(true);
      setTimeout(() => setCopiedActions(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="space-y-5">

      {/* Header summary card */}
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-forest-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <BrainCircuit className="w-4.5 h-4.5 text-forest-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-forest-700 uppercase tracking-wide">MAVIS Prep Brief</p>
              <p className="text-[11px] text-app-muted">
                Generated {new Date(generatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          {brief.deal_health && <DealHealthChip health={brief.deal_health} />}
        </div>

        <p className="text-sm text-app-ink leading-relaxed mb-4">
          {brief.executive_summary}
        </p>

        {brief.deal_health_reasoning && (
          <p className="text-xs text-app-muted italic border-t border-app-line pt-3">
            {brief.deal_health_reasoning}
          </p>
        )}
      </div>

      {/* Confidence + reasoning */}
      <SectionCard title="Brief Confidence" icon={Target}>
        <ConfidenceMeter value={brief.confidence ?? 0} />
        {brief.reasoning_notes && (
          <p className="text-xs text-app-muted mt-3 leading-relaxed">{brief.reasoning_notes}</p>
        )}
      </SectionCard>

      {/* Forecast assessment */}
      {brief.forecast_assessment && (
        <SectionCard title="Forecast Assessment" icon={TrendingUp}>
          <p className="text-sm text-app-ink leading-relaxed">{brief.forecast_assessment}</p>
        </SectionCard>
      )}

      {/* Qualification gaps */}
      {gaps.length > 0 && (
        <SectionCard title={`Qualification Gaps (${gaps.length})`} icon={ShieldAlert}>
          <div className="space-y-3">
            {gaps.map((gap: PrepBriefQualificationGap, i: number) => (
              <div key={i} className="flex gap-3 p-3 bg-app-canvas rounded-xl">
                <SeverityChip severity={gap.severity} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-app-ink mb-0.5">{gap.field}</p>
                  <p className="text-xs text-app-muted leading-relaxed">{gap.recommendation}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Stakeholder risks */}
      {risks.length > 0 && (
        <SectionCard title={`Stakeholder Risks (${risks.length})`} icon={Users}>
          <div className="space-y-3">
            {risks.map((risk: PrepBriefStakeholderRisk, i: number) => (
              <div key={i} className="p-3 bg-app-canvas rounded-xl">
                <p className="text-xs font-semibold text-app-ink mb-0.5">{risk.name}</p>
                <p className="text-xs text-app-muted mb-1.5">{risk.risk}</p>
                <div className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 text-forest-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-forest-700 font-medium">{risk.action}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Recommended questions */}
      {questions.length > 0 && (
        <SectionCard
          title="Recommended Questions"
          icon={HelpCircle}
          action={<CopyButton onCopy={copyQuestions} copied={copiedQuestions} />}
        >
          <ol className="space-y-2">
            {questions.map((q: string, i: number) => (
              <li key={i} className="flex gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-forest-50 text-forest-700 text-[11px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-app-ink leading-relaxed">{q}</p>
              </li>
            ))}
          </ol>
        </SectionCard>
      )}

      {/* Next actions */}
      {actions.length > 0 && (
        <SectionCard
          title="Recommended Next Actions"
          icon={CheckCircle2}
          action={<CopyButton onCopy={copyActions} copied={copiedActions} />}
        >
          <div className="space-y-3">
            {actions.map((a: PrepBriefNextAction, i: number) => (
              <div key={i} className="flex gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-forest-500 flex items-center justify-center mt-0.5">
                  <CheckCircle2 className="w-3 h-3 text-app-surface" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-ink leading-snug">{a.action}</p>
                  <p className="text-xs text-app-muted mt-0.5 leading-relaxed">{a.rationale}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Meeting prep notes */}
      {brief.meeting_prep_notes && (
        <SectionCard title="Meeting Prep Notes" icon={Lightbulb}>
          <p className="text-sm text-app-ink leading-relaxed whitespace-pre-line">
            {brief.meeting_prep_notes}
          </p>
        </SectionCard>
      )}
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────

interface Props {
  opportunityId: string;
  orgId: string;
  accountId: string;
  onForecastApplied?: (patch: Record<string, unknown>) => void;
  onStakeholderAdded?: (s: MavisStakeholder) => void;
  onQualificationApplied?: (column: string, merged: Record<string, unknown>) => void;
}

export default function MavisPrepBriefPanel({
  opportunityId,
  orgId: _orgId,
  accountId,
  onForecastApplied,
  onStakeholderAdded,
  onQualificationApplied,
}: Props) {
  const [brief,       setBrief]       = useState<MavisOpportunityPrepBrief | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading,     setLoading]     = useState(true);  // initial DB fetch
  const [generating,  setGenerating]  = useState(false); // AI generation
  const [error,       setError]       = useState<string | null>(null);

  // Load latest saved brief on mount
  useEffect(() => {
    let cancelled = false;

    const loadLatest = async () => {
      setLoading(true);
      try {
        const { brief: data } = await getPrepBrief(opportunityId);

        if (cancelled) return;
        if (data) {
          setBrief(data.brief_json as MavisOpportunityPrepBrief);
          setGeneratedAt(data.created_at);
        }
      } catch {
        // silent — just show empty state, user can generate fresh
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadLatest();
    return () => { cancelled = true; };
  }, [opportunityId]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);

    try {
      const data = await generatePrepBrief(opportunityId).catch((err: unknown) => {
        throw toUserError(err);
      });

      if (!data.brief) {
        throw new Error('No brief returned. Please try again.');
      }

      setBrief(data.brief as MavisOpportunityPrepBrief);
      setGeneratedAt(data.generated_at ?? new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate brief. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [opportunityId]);

  // ── Initial DB load spinner ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
        <div className="py-16 text-center px-6">
          <Loader2 className="w-7 h-7 text-app-muted animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────

  if (!brief && !generating) {
    return (
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
        <div className="py-16 text-center px-6">
          <div className="w-14 h-14 bg-forest-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BrainCircuit className="w-7 h-7 text-forest-500" />
          </div>
          <p className="text-base font-semibold text-app-ink mb-1.5">MAVIS Opportunity Prep</p>
          <p className="text-sm text-app-muted max-w-sm mx-auto mb-6 leading-relaxed">
            MAVIS will analyse your qualification data, stakeholders, account context, and
            vendor intelligence to generate a structured deal prep brief.
          </p>
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-4 max-w-sm mx-auto text-left">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
          <button onClick={handleGenerate} className="btn-primary !px-6">
            <BrainCircuit className="w-4 h-4 mr-2" />
            Prepare with MAVIS
          </button>
        </div>
      </div>
    );
  }

  // ── AI generation spinner ──────────────────────────────────────────────────

  if (generating) {
    return (
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
        <div className="py-16 text-center px-6">
          <div className="w-14 h-14 bg-forest-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-7 h-7 text-forest-500 animate-spin" />
          </div>
          <p className="text-base font-semibold text-app-ink mb-1.5">Preparing your brief…</p>
          <p className="text-sm text-app-muted max-w-xs mx-auto leading-relaxed">
            MAVIS is analysing qualification data, stakeholders, and account intelligence.
            This takes 10–20 seconds.
          </p>
        </div>
      </div>
    );
  }

  // ── Brief display ──────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-app-muted">
          Brief saved {generatedAt
            ? new Date(generatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
            : ''}
        </p>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-1.5 text-sm text-forest-600 hover:text-forest-700 font-medium transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Regenerate brief
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      <PrepBriefDisplay brief={brief!} generatedAt={generatedAt!} />

      {brief!.apply_suggestions ? (
        <MavisApplySuggestionsPanel
          opportunityId={opportunityId}
          orgId={_orgId}
          accountId={accountId}
          suggestions={brief!.apply_suggestions}
          onForecastApplied={onForecastApplied}
          onStakeholderAdded={onStakeholderAdded}
          onQualificationApplied={onQualificationApplied}
        />
      ) : (
        <div className="bg-app-canvas rounded-2xl border border-app-line px-5 py-4 text-center">
          <p className="text-sm text-app-muted">
            Regenerate the brief to get apply-ready suggestions.
          </p>
        </div>
      )}
    </div>
  );
}
